import { type ReactNode, createContext, use, useMemo } from "react";
import { Info } from "lucide-react";
import type { ClaimResponse } from "@patent-studio/api-types";
import { Panel } from "@/components/ui/Panel";
import { useFigures } from "@/hooks/use-figures";
import { definitionsOf, drawnOn } from "@/lib/element-registry";
import { claimReferences } from "./claim-references";
import type { ReferenceSource } from "@/components/ui/StructuredText";

/** What a claim's words can name, and a value that moves when the drawings do. */
interface Drawn {
  references: ReferenceSource;
  /**
   * The registry's own version, for the editors to redraw their numerals on; -1
   * until the drawings arrive, so a registry served at its first version, 0,
   * still redraws them.
   */
  version: number;
  /** No drawing carries a pin yet, so every numeral a claim shows comes from the registry alone. */
  provisional: boolean;
}

/** What a claim can name before the drawings arrive, which `claimReferences` already spells. */
const NOTHING: Drawn = {
  references: claimReferences(undefined),
  version: -1,
  provisional: false,
};

const DrawnContext = createContext<Drawn>(NOTHING);

/**
 * The drawn elements, asked for once for the whole set: forty rows asking for the
 * same registry is forty answers to keep in step, and the last to arrive wins.
 *
 * The lighter of the two figure hooks on purpose. `useFigureDefinitions` holds a
 * write queue and its own notion of what is unsaved, and says in its own doc that
 * a second instance shows a view missing the first one's changes. Nothing writes
 * here: a claim reads the registry and cites it.
 *
 * `open` is where a drawn element's numeral goes when pressed: its first drawing.
 * The page passes it, so this reads the drawings and knows nothing of routes.
 */
export function DrawnElements({
  taskId,
  open,
  children,
}: {
  taskId: string;
  open?: (elementId: string) => void;
  children: ReactNode;
}) {
  const figures = useFigures(taskId);
  const served = figures.data?.definitions;
  // Keyed on what was served, not on the object built from it: `definitionsOf`
  // answers anew every call, so a memo over its result holds for one render.
  const drawn = useMemo((): Drawn => {
    const definitions = definitionsOf(served);
    return {
      references: claimReferences(definitions, open),
      version: served === undefined ? -1 : definitions.version,
      provisional:
        served !== undefined &&
        definitions.registry.length > 0 &&
        drawnOn(definitions).size === 0,
    };
  }, [served, open]);
  return <DrawnContext value={drawn}>{children}</DrawnContext>;
}

/** What this claim set can name; nothing until the drawings arrive, so a claim writes the same. */
export function useDrawnElements() {
  return use(DrawnContext);
}

/**
 * v0.28's bar over a claim set whose numerals no drawing can check yet: shown
 * while no figure carries a pin and some claim names an element with a numeral.
 * It is what keeps a provisional numeral honest about the sheet (R272).
 */
export function ProvisionalNumerals({ claims }: { claims: ClaimResponse[] }) {
  const { references, provisional } = useDrawnElements();
  const shows = claims.some((claim) =>
    claim.lines.some((line) =>
      line.runs.some(
        (run) =>
          typeof run.reference === "string" &&
          references.tagOf(run.reference) !== null,
      ),
    ),
  );
  if (!provisional || !shows) return null;
  return (
    <Panel
      role="status"
      tone="provisional"
      className="mt-3 flex items-start gap-2 text-xs leading-[1.5]"
    >
      <Info className="mt-px size-[15px] flex-none" aria-hidden />
      <span>
        <b>Reference numerals are provisional.</b> The numerals in these claims
        come from the element registry. No figure has been numbered yet, so none
        of them can be checked against a drawing. Numerals are optional and
        never change what a claim covers; each claim is checked against the
        drawings once the figures are numbered in <b>Figures</b>.
      </span>
    </Panel>
  );
}
